import { useFieldApi, useFieldState } from 'informed';
import type { Form } from 'payments-lib-types';
import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { getErrorTranslation } from '@/components/form/utils';
import { maskExpirationDate } from '@/helpers/masks';
import {
  cardExpiryDateValidator,
  type ValidatorMessage,
  validateCardExpiryDate,
  validateCardExpiryMonth,
  validateCardExpiryYear,
} from '@/helpers/validators';
import type { OfUnion } from '@/types/common';
import type { MethodField } from '../helpers';

export const FieldCardExpiryDate: FC<
  OfUnion<MethodField>[Form.MethodFieldType.CardExpiryDate]
> = ({ fieldName, label, activeThemeMode }) => {
  const { t } = useTranslation('common');

  const fieldApi = useFieldApi(fieldName);

  const monthFieldState = useFieldState('month');
  const yearFieldState = useFieldState('year');

  const expiryDate = useMemo(
    () =>
      monthFieldState?.value && yearFieldState?.value
        ? `${monthFieldState.value}/${yearFieldState.value}`
        : undefined,
    [monthFieldState?.value, yearFieldState?.value]
  );

  useEffect(() => {
    if (expiryDate) {
      fieldApi.setValue(expiryDate);
    }
  }, [fieldApi, expiryDate]);

  const errorField = useMemo(() => {
    if (
      monthFieldState?.value?.toString().length === 2 &&
      yearFieldState?.value?.toString().length === 2
    ) {
      const message = cardExpiryDateValidator(expiryDate, undefined);
      return typeof message === 'string' ? message : null;
    }

    if (!(monthFieldState?.touched && yearFieldState?.touched)) return null;
    return validateCardExpiryDate(expiryDate, undefined) as string;
  }, [expiryDate, monthFieldState, yearFieldState]);

  return (
    <FormGroup mode={activeThemeMode} title={label || fieldName}>
      <div className="fields">
        <Input
          formatter={maskExpirationDate}
          hideErrorMessage
          mode={activeThemeMode}
          name="month"
          placeholder={t('date.month')}
          validate={validateCardExpiryMonth}
        />
        /
        <Input
          formatter={maskExpirationDate}
          hideErrorMessage
          mode={activeThemeMode}
          name="year"
          placeholder={t('date.year')}
          validate={validateCardExpiryYear}
        />
        <div className="hidden-field">
          <Input disabled name={fieldName} type="hidden" />
        </div>
      </div>
      {errorField && (
        <div className="error">
          {getErrorTranslation(t, errorField as ValidatorMessage)}
        </div>
      )}
    </FormGroup>
  );
};
